// Alteration tickets for the Fashion layout (server: s_service_request rows of
// the 'Alteration' service type, through createFashionAlteration,
// getFashionAlterations, setFashionAlterationStatus). A ticket belongs to a
// customer: the one on the bill, or the one on file with the mobile given.
// A garment is taken in (RCV), worked on (INP), ready for pick-up (RDY), then
// handed over (DLV); it can be cancelled (CAN) while open. The charge is
// recorded on the ticket only; nothing here touches a bill or its payments.
import dayjs from "dayjs";

export const STATUS_LABEL = { RCV: "Taken in", INP: "In work", RDY: "Ready", DLV: "Handed over", CAN: "Cancelled" };

// Same moves the server allows (FashionRules.ALTERATION_NEXT), in button order.
export const MOVES = {
  RCV: [
    { to: "INP", label: "Start work" },
    { to: "RDY", label: "Mark ready" },
    { to: "CAN", label: "Cancel" },
  ],
  INP: [
    { to: "RDY", label: "Mark ready" },
    { to: "CAN", label: "Cancel" },
  ],
  RDY: [
    { to: "DLV", label: "Hand over" },
    { to: "INP", label: "Back to work" },
    { to: "CAN", label: "Cancel" },
  ],
};

// List filters: OPEN is RCV + INP + RDY on the server.
export const FILTERS = [
  { value: "OPEN", label: "Open" },
  { value: "RDY", label: "Ready" },
  { value: "DLV", label: "Handed over" },
  { value: "CAN", label: "Cancelled" },
];

// Server status filters that together cover every ticket (there is no "all"
// on the server), so a search finds a ticket whatever its stage.
export const ALL_STATUSES = ["OPEN", "DLV", "CAN"];
// Handed-over and cancelled lists grow forever; ask for the server's maximum.
export const LIST_LIMIT = 200;

// Tabs on the screen, filtered on the till from the merged list.
export const TABS = [
  { value: "ALL", label: "All", match: () => true },
  { value: "RCV", label: "Taken in", match: (s) => s === "RCV" },
  { value: "INP", label: "In work", match: (s) => s === "INP" },
  { value: "RDY", label: "Ready", match: (s) => s === "RDY" },
  { value: "DLV", label: "Handed over", match: (s) => s === "DLV" },
  { value: "CAN", label: "Cancelled", match: (s) => s === "CAN" },
];

// The stages a ticket goes through, for the detail's progress line.
export const STEPS = ["RCV", "INP", "RDY", "DLV"];

// "+91 98480-12345" → "9848012345": digits only, and the country code dropped
// when a 10-digit Indian number was typed with it.
export function normaliseMobile(raw) {
  const d = String(raw ?? "").replace(/\D/g, "");
  return d.length === 12 && d.startsWith("91") ? d.slice(2) : d;
}

// What to send as a search: a phone number typed with spaces, dashes or +91
// becomes its digits; anything else (bill no., name) goes as typed.
export function searchTerm(raw) {
  const t = clean(raw);
  if (!t) return "";
  if (/^[+\d\s().-]+$/.test(t) && /\d/.test(t)) return normaliseMobile(t) || t;
  return t;
}

// Rows from several status lists → one list, each ticket once, open work first
// then by ready-by date.
const RANK = { RCV: 0, INP: 0, RDY: 1, DLV: 2, CAN: 3 };
export function mergeAlterations(lists) {
  const seen = new Map();
  (lists || []).forEach((rows) => (rows || []).forEach((r) => r?.id && !seen.has(r.id) && seen.set(r.id, r)));
  return [...seen.values()].sort((a, b) => (RANK[a.status] ?? 9) - (RANK[b.status] ?? 9) || String(a.promisedDate).localeCompare(String(b.promisedDate)));
}

export function countByStatus(rows) {
  const out = { RCV: 0, INP: 0, RDY: 0, DLV: 0, CAN: 0 };
  (rows || []).forEach((r) => {
    if (r?.status in out) out[r.status] += 1;
  });
  return out;
}

// Column sizes on s_service_request: notes go in customer_complaint, the bill
// number in reference_no.
export const MAX_NOTES = 255;
export const MAX_BILL_NO = 30;

// A tailor picked from the store's list goes in s_service_request.s_technician_id
// (tailorId). A tailor typed by hand, when the store has none on file, rides at
// the front of the notes as "Tailor: Name · notes" and is split off again when read.
const TAILOR_RE = /^Tailor: ([^·]+?)(?: · (.*))?$/;
export const MAX_TAILOR = 40;
export function withTailor(notes, tailor) {
  const t = clean(tailor).replace(/·/g, " ").replace(/ +/g, " ").trim();
  const n = clean(notes);
  if (!t) return n;
  return n ? `Tailor: ${t} · ${n}` : `Tailor: ${t}`;
}
export function splitTailor(raw) {
  const s = String(raw ?? "");
  const m = s.match(TAILOR_RE);
  return m ? { tailor: m[1].trim(), notes: (m[2] || "").trim() } : { tailor: "", notes: s };
}

export const ALTERATION_TYPES = ["Hem", "Shorten sleeves", "Take in", "Let out", "Taper", "Fit adjustment", "Button / zip"];

// A ticket can carry several jobs on one garment; they are kept in the one
// work field as "Hem + Taper" (s_service_request.issue_description, 255 chars).
export const WORK_JOIN = " + ";
export const MAX_WORK = 255;
export function workParts(work) {
  return String(work ?? "")
    .split("+")
    .map((p) => p.trim())
    .filter(Boolean);
}
export function hasWork(work, type) {
  return workParts(work).some((p) => p.toLowerCase() === String(type).toLowerCase());
}
// Adds the job, or takes it off when it is already there.
export function toggleWork(work, type) {
  const parts = workParts(work);
  const next = hasWork(work, type) ? parts.filter((p) => p.toLowerCase() !== String(type).toLowerCase()) : [...parts, type];
  return next.join(WORK_JOIN).slice(0, MAX_WORK);
}

const clean = (s) => String(s ?? "").replace(/["\\]/g, "").replace(/[\r\n]+/g, " ").trim();
const str = (s) => `"${clean(s)}"`;
const FIELDS = "alterationId documentNo orderId orderDocumentNo productId productName customerId customerName mobileNo alterationType notes charge promisedDate status created deliveredAt tailorId tailorName";

export function buildAlterationsQuery(bunitId, status = "OPEN", search = "", limit = 0) {
  const term = clean(search);
  return {
    query: `query { getFashionAlterations(bunitId: ${str(bunitId)}, status: ${str(status || "OPEN")}${term ? `, search: ${str(term)}` : ""}${limit > 0 ? `, limit: ${Math.floor(limit)}` : ""}) { ${FIELDS} }}`,
  };
}

// The store's priced jobs (s_joborder_charge_config) and tailors (s_technician)
// for the take-in form.
export function buildAlterationOptionsQuery(bunitId) {
  return { query: `query { getFashionAlterationOptions(bunitId: ${str(bunitId)}) { operations { id name rate category } tailors { id code name } }}` };
}

// Server response → { operations, tailors }, or null when the call failed.
export function readAlterationOptions(response) {
  if (!response || response?.data?.errors?.length) return null;
  const o = response?.data?.data?.getFashionAlterationOptions;
  if (!o) return null;
  return {
    operations: (o.operations || []).filter((op) => op?.id && op.name).map((op) => ({ id: op.id, name: op.name, rate: Number(op.rate) || 0, category: op.category || "" })),
    tailors: (o.tailors || []).filter((t) => t?.id && t.name).map((t) => ({ id: t.id, code: t.code || "", name: t.name })),
  };
}

// Picked jobs → the work ("Hem, Taper") and the charge (sum of their rates).
export const OPERATION_JOIN = ", ";
export function applyOperations(operations, pickedIds) {
  const picked = (operations || []).filter((op) => (pickedIds || []).includes(op.id));
  const charge = Math.round(picked.reduce((sum, op) => sum + (Number(op.rate) || 0), 0) * 100) / 100;
  return { alterationType: picked.map((op) => op.name).join(OPERATION_JOIN).slice(0, MAX_WORK), charge };
}

// Bills to take a garment in from: the same salesHistory search Sales History
// uses (bill no., customer code, mobile or name), across all tills and dates.
export function buildBillSearchQuery(search, limit = 10) {
  const term = searchTerm(search);
  const sort = JSON.stringify({ dateordered: "desc", documentno: "desc" }).replace(/"/g, '\\"');
  return {
    query: `query {salesHistory(
      q: ${str(term)},
      filter_by: "{\\"DateRange\\":[null,null]}",
      startRow: "0",
      endRow: "${Math.max(1, Math.floor(limit))}",
      sort_by: "${sort}",
      tillId: null,
      layAway: "N"
    ) { sOrderID documentno dateordered grosstotal isReturn cwrB2cCustomer { cwrCustomerId name mobileNo } }}`,
  };
}

// Server rows → sale bills (returns can't be altered), or null when the call failed.
export function readFoundBills(response) {
  const rows = response?.data?.data?.salesHistory;
  if (!Array.isArray(rows)) return null;
  return rows
    .filter((r) => r?.sOrderID && r.isReturn !== "Y" && r.isReturn !== true)
    .map((r) => ({
      orderId: r.sOrderID,
      documentNo: r.documentno || "",
      date: r.dateordered ? String(r.dateordered).slice(0, 10) : "",
      total: Number(r.grosstotal) || 0,
      customerId: r.cwrB2cCustomer?.cwrCustomerId || null,
      customerName: r.cwrB2cCustomer?.name || "",
      mobileNo: r.cwrB2cCustomer?.mobileNo || "",
    }));
}

export function buildFoundBillLinesQuery(orderId) {
  return { query: `query {salesHistoryDetails(orderId: ${str(orderId)}) { sOrderID line { qty product { mProductId name value } } }}` };
}

// Bill lines → the items on it, each once, for the "which item" choice.
export function readFoundBillLines(response) {
  const lines = response?.data?.data?.salesHistoryDetails?.[0]?.line;
  if (!Array.isArray(lines)) return null;
  const seen = new Set();
  return lines
    .filter((l) => l?.product?.mProductId && Number(l.qty) > 0)
    .filter((l) => (seen.has(l.product.mProductId) ? false : seen.add(l.product.mProductId)))
    .map((l) => ({ productId: l.product.mProductId, name: l.product.name || l.product.value || l.product.mProductId }));
}

export function buildCreateAlterationMutation(a) {
  const fields = [
    `bunitId: ${str(a.bunitId)}`,
    `alterationType: ${str(a.alterationType)}`,
    `promisedDate: ${str(a.promisedDate)}`,
    `charge: ${Number(a.charge) || 0}`,
    a.orderId ? `orderId: ${str(a.orderId)}` : null,
    a.orderDocumentNo ? `orderDocumentNo: ${str(a.orderDocumentNo)}` : null,
    a.productId ? `productId: ${str(a.productId)}` : null,
    a.customerId ? `customerId: ${str(a.customerId)}` : null,
    a.customerName ? `customerName: ${str(a.customerName)}` : null,
    a.mobileNo ? `mobileNo: ${str(a.mobileNo)}` : null,
    a.notes ? `notes: ${str(a.notes)}` : null,
    a.tailorId ? `tailorId: ${str(a.tailorId)}` : null,
  ].filter(Boolean);
  return { query: `mutation { createFashionAlteration(input: { ${fields.join(", ")} }) { status message recordId documentNo } }` };
}

export function buildAlterationStatusMutation(alterationId, status) {
  return { query: `mutation { setFashionAlterationStatus(alterationId: ${str(alterationId)}, status: ${str(status)}) { status message recordId documentNo } }` };
}

// Server response → alteration rows, or null when the call failed.
export function readAlterations(response) {
  if (!response || response?.data?.errors?.length) return null;
  const rows = response?.data?.data?.getFashionAlterations;
  if (!Array.isArray(rows)) return null;
  return rows.map((r) => ({
    id: r.alterationId,
    documentNo: r.documentNo || "",
    orderId: r.orderId || null,
    orderDocumentNo: r.orderDocumentNo || "",
    productId: r.productId || null,
    productName: r.productName || "",
    customerId: r.customerId || null,
    customerName: r.customerName || "",
    mobileNo: r.mobileNo || "",
    type: r.alterationType || "",
    ...splitTailor(r.notes || ""),
    ...(r.tailorName ? { tailor: r.tailorName } : {}),
    tailorId: r.tailorId || null,
    charge: Number(r.charge) || 0,
    promisedDate: r.promisedDate ? String(r.promisedDate).slice(0, 10) : "",
    status: r.status,
    deliveredAt: r.deliveredAt || "",
  }));
}

export const defaultPromisedDate = (now = new Date()) => dayjs(now).add(3, "day").format("YYYY-MM-DD");

// Not ready yet and the promised date has passed.
export const isOverdue = (alt, now = new Date()) => (alt?.status === "RCV" || alt?.status === "INP") && Boolean(alt.promisedDate) && alt.promisedDate < dayjs(now).format("YYYY-MM-DD");

// Items being sold on the current bill, for the form's item list.
export function billLines(cart) {
  const seen = new Set();
  return (cart?.items || [])
    .filter((i) => i?.productId && Number(i.weight) > 0 && i.isReturn !== true && i.isReturn !== "Y")
    .filter((i) => (seen.has(i.productId) ? false : seen.add(i.productId)))
    .map((i) => ({ productId: i.productId, name: i.name || i.value || i.productId }));
}

// Form values → { alteration } ready for buildCreateAlterationMutation, or { error }.
// draft.itemId is a product from the current bill (cart) or "" for none.
// foundBill (optional) is a past bill picked from readFoundBills, with its
// items in foundBill.lines; it then takes the place of the current bill.
export function validateAlterationDraft(draft, cart, now = new Date(), foundBill = null) {
  const type = clean(draft?.alterationType);
  if (!type) return { error: "Say what alteration is needed, e.g. Hem." };
  const name = clean(draft?.customerName) || clean(foundBill?.customerName);
  const mobile = clean(draft?.mobileNo) || clean(foundBill?.mobileNo);
  const customerId = draft?.customerId || foundBill?.customerId || null;
  if (!customerId && !mobile) return { error: "Add the customer to the bill, or their mobile number, so the garment can be handed back." };
  if (mobile && !/^\+?[0-9 ()-]{6,18}$/.test(mobile)) return { error: "Enter the mobile number as digits." };
  if (clean(draft?.tailor).length > MAX_TAILOR) return { error: `Keep the tailor's name to ${MAX_TAILOR} characters.` };
  const notes = withTailor(draft?.notes, draft?.tailor);
  if (notes.length > MAX_NOTES) return { error: `Keep the notes to ${MAX_NOTES} characters.` };
  if (clean(draft?.billNo).length > MAX_BILL_NO) return { error: `A bill number is at most ${MAX_BILL_NO} characters.` };
  const rawCharge = String(draft?.charge ?? "").trim();
  const charge = rawCharge === "" ? 0 : Number(rawCharge);
  if (!Number.isFinite(charge) || charge < 0) return { error: "Enter the charge as an amount, or leave it blank for free." };
  const promised = dayjs(draft?.promisedDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft?.promisedDate || "") || !promised.isValid()) return { error: "Choose when it will be ready." };
  if (promised.format("YYYY-MM-DD") < dayjs(now).format("YYYY-MM-DD")) return { error: "The ready date can't be in the past." };
  if (foundBill) {
    const item = (foundBill.lines || []).find((l) => l.productId === draft?.itemId);
    if ((foundBill.lines || []).length > 0 && !item) return { error: "Choose which item on the bill needs the alteration." };
    return {
      alteration: {
        alterationType: type,
        customerId,
        customerName: name,
        mobileNo: normaliseMobile(mobile),
        productId: item?.productId || null,
        orderId: foundBill.orderId || null,
        orderDocumentNo: clean(foundBill.documentNo),
        notes,
        charge: Math.round(charge * 100) / 100,
        promisedDate: promised.format("YYYY-MM-DD"),
        tailorId: draft?.tailorId || null,
      },
    };
  }
  const onBill = draft?.itemId ? billLines(cart).find((l) => l.productId === draft.itemId) : null;
  return {
    alteration: {
      alterationType: type,
      customerId,
      customerName: name,
      mobileNo: normaliseMobile(mobile),
      productId: onBill?.productId || null,
      orderId: onBill ? cart?.sOrderID || null : null,
      orderDocumentNo: onBill ? clean(cart?.documentno) : clean(draft?.billNo),
      notes,
      charge: Math.round(charge * 100) / 100,
      promisedDate: promised.format("YYYY-MM-DD"),
      tailorId: draft?.tailorId || null,
    },
  };
}
